// app/ask-cimigo/components/CurrencySwitcher.tsx
"use client";
import Image from "next/image";
import image from "@/constants/image";
import Box from "@mui/material/Box";
import UserServices from "@/services/main/UserServices";
import { useAppDispatch, useAppSelector } from "@/lib/hooks";
import { setCurrencyLocal } from "@/lib/features/authSlice";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { useSetCookie } from "cookies-next";
import { cookies } from "@/utils/auth";
import { User } from "@/models/user";

export default function CurrencySwitcher({
  currencySSR,
  // setCurrency,
  isLoggedIn,
}: {
  currencySSR: "VND" | "USD" | "IDR" | string;
  // setCurrency: (value: "VND" | "USD" | "IDR") => void;
  isLoggedIn: boolean;
}) {
  const setCookie = useSetCookie();
  const queryClient = useQueryClient();
  const t = useTranslations("messages");
  const dispatch = useAppDispatch();
  const { currencyLocal } = useAppSelector((state) => state.auth);
  const user = queryClient.getQueryData<User>(["user"]);
  const [currency, setCurrency] = useState<"VND" | "USD" | "IDR" | null>(currencySSR as "VND" | "USD" | "IDR");

  useEffect(() => {
    if (currencyLocal) {
      setCurrency(currencyLocal as "VND" | "USD" | "IDR");
    } else {
      if (user?.currency) {
        dispatch(setCurrencyLocal(user?.currency as "VND" | "USD" | "IDR"));
        return;
      }
      const localCurrency = cookies.get("currency");
      if (localCurrency) {
        dispatch(setCurrencyLocal(localCurrency as "VND" | "USD" | "IDR"));
        setCurrency(localCurrency as "VND" | "USD" | "IDR");
      }
    }
  }, [user, currencyLocal, dispatch]);

  const changeCurrency = async (currency: "VND" | "USD" | "IDR") => {
    dispatch(setCurrencyLocal(currency));
    await UserServices.changeCurrency(currency).then(() => {
      queryClient.setQueryData(["user"], (oldData: any) => {
        return {
          ...oldData,
          currency: currency,
        };
      });
      setCookie("currency", currency, {
        maxAge: 365 * 24 * 60 * 60,
        path: "/"
      });
    });
  };

  const handleCombineChangeCurrecy = (currencyTemp: "VND" | "USD" | "IDR") => {
    if (!isLoggedIn) {
      dispatch(setCurrencyLocal(currencyTemp));
      setCookie("currency", currencyTemp, {
        maxAge: 365 * 24 * 60 * 60,
        path: "/"
      });
      return;
    }
    changeCurrency(currencyTemp);
  };

  const currencies = [
    {
      code: "USD" as const,
      icon: image.iconNY,
      alt: "NY Flag"
    },
    {
      code: "VND" as const,
      icon: image.iconVietNam,
      alt: "Vietnam Flag"
    },
    {
      code: "IDR" as const,
      icon: image.iconID,
      alt: "ID Flag"
    }
  ];

  return (
    <Box className=" flex flex-row gap-4 items-center">
      <p
        className="m-0  hidden sm:flex text-gray-90 text-sm/6 "
        translation-key="common_Currency"
      >
        {t("common_Currency")}
      </p>
      <Box className=" flex items-center gap-2">
        {currencies.map(({ code, icon, alt }) => (
          <button
            key={code}
            onClick={() => handleCombineChangeCurrecy(code)}
            className={`pricing-switch-currency-button border-0 flex items-center gap-2 px-2 py-1 rounded-[4px] transition cursor-pointer
            ${currency as "VND" | "USD" | "IDR" === code ? "bg-cimigo-blue-light-4" : "bg-white"}`}
          >
            <span
              className={`text-sm/6 font-semibold ${currency as "VND" | "USD" | "IDR" === code ? "text-cimigo-blue" : "text-eerie_black"
                }`}
            >
              {code}
            </span>
            <Image
              src={icon}
              alt={alt}
              className="w-5 h-3.5 object-cover rounded-sm"
            />
          </button>
        ))}
      </Box>
    </Box>
  );
}
